<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div
      class="navbar-header expanded"
      style="height:80px"
    >
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li
            class="nav-item mr-auto "
            style=""
          >
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span style="display: block; margin: auto;">
                <b-img
                  :src="appLogoImage"
                  alt="logo"
                  width="90px"
                />
              </span>
              <!--              <h2 class="brand-text">-->
              <!--                {{ appName }}-->
              <!--              </h2>-->
            </b-link>
          </li>

          <li class="nav-item nav-toggle">
            <b-link
              class="nav-link modern-nav-toggle d-block d-xl-none"
              @click.prevent="toggleVerticalMenuActive"
            >
              <feather-icon
                icon="XIcon"
                size="20"
              />
            </b-link>
            <b-link
              class="nav-link modern-nav-toggle d-none d-xl-block"
              @click.prevent="toggleCollapsed"
            >
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="collapse-toggle-icon"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Search box -->
    <div class="menu-search px-1 py-1">
      <b-input-group class="input-group-merge">
        <b-input-group-prepend is-text>
          <feather-icon
            icon="SearchIcon"
            size="14"
          />
        </b-input-group-prepend>
        <b-form-input
          v-model="search"
          placeholder="جستجو در منو..."
          class="form-control-merge"
        />
        <b-input-group-append
          v-if="search"
          is-text
          class="cursor-pointer"
          @click="search = ''"
        >
          <feather-icon
            icon="XIcon"
            size="14"
            class="cursor-pointer"
          />
        </b-input-group-append>
      </b-input-group>
    </div>

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        v-if="filteredNavMenuItems.length"
        :items="filteredNavMenuItems"
        class="navigation navigation-main"
      />
      <li
        v-else-if="search"
        class="nav-item no-results text-center py-3 px-1 text-muted"
        style="list-style: none;"
      >
        <feather-icon
          icon="InboxIcon"
          size="22"
          class="mb-50 text-muted d-block mx-auto"
        />
        <small class="font-small-3">موردی یافت نشد</small>
      </li>
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical'
import router from '@/router'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {
  BLink,
  BImg,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BInputGroupPrepend,
} from 'bootstrap-vue'
import { provide, computed, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    BInputGroupAppend,
    BInputGroupPrepend,
    BInputGroup,
    BFormInput,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const search = ref('')
    provide('menuSearchQuery', search)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const normalizeText = text => {
      if (!text) return ''
      return text
        .toString()
        .toLowerCase()
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/ي/g, 'ی')
        .replace(/ك/g, 'ک')
        .replace(/[\u064B-\u065F]/g, '')
        .trim()
    }

    const routeMetaCache = new WeakMap()

    const getItemSearchableText = item => {
      if (routeMetaCache.has(item)) {
        return routeMetaCache.get(item)
      }

      const parts = []

      // 1. Menu item title, header, tags
      if (item.title) parts.push(item.title)
      if (item.header) parts.push(item.header)
      if (item.tag) parts.push(item.tag)

      // 2. Resource, Service, Keywords
      if (item.resource) {
        parts.push(item.resource)
        parts.push(item.resource.replace(/([a-z])([A-Z])/g, '$1 $2'))
      }
      if (item.service) parts.push(item.service)
      if (item.keywords) {
        parts.push(Array.isArray(item.keywords) ? item.keywords.join(' ') : item.keywords)
      }

      // 3. Target route details (path, route name, pageTitle, breadcrumbs, route meta)
      if (item.route) {
        try {
          const resolved = router.resolve(item.route)
          if (resolved && resolved.route) {
            const { path, name, meta = {} } = resolved.route
            if (path && path !== '/') {
              parts.push(path)
              parts.push(path.replace(/[/_-]/g, ' '))
            }
            if (name) {
              parts.push(name)
              parts.push(name.replace(/[-_]/g, ' '))
            }
            if (meta.pageTitle) parts.push(meta.pageTitle)
            if (meta.resource && meta.resource !== item.resource) {
              parts.push(meta.resource)
              parts.push(meta.resource.replace(/([a-z])([A-Z])/g, '$1 $2'))
            }
            if (Array.isArray(meta.breadcrumb)) {
              meta.breadcrumb.forEach(b => {
                const bText = typeof b === 'string' ? b : (b.text || b.title)
                if (bText) parts.push(bText)
              })
            }
          }
        } catch (e) {
          // Ignore unresolvable routes
        }
      }

      const combinedText = normalizeText(parts.join(' '))
      routeMetaCache.set(item, combinedText)
      return combinedText
    }

    const filterItems = (items, query) => items
      .map(item => {
        const itemSearchText = getItemSearchableText(item)
        const isMatch = itemSearchText.includes(query)

        if (Array.isArray(item.children) && item.children.length) {
          const filteredChildren = filterItems(item.children, query)

          if (isMatch) {
            return { ...item, children: item.children }
          }

          if (filteredChildren.length) {
            return { ...item, children: filteredChildren }
          }

          return null
        }

        return isMatch ? item : null
      })
      .filter(Boolean)

    const filteredNavMenuItems = computed(() => {
      const query = normalizeText(search.value)
      if (!query) return navMenuItems
      return filterItems(navMenuItems, query)
    })

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Search
      search,
      filteredNavMenuItems,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.main-menu {
  .menu-search {
    height: 52px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    .input-group-merge {
      border-radius: 0.357rem;
      width: 100%;

      .input-group-text {
        padding: 0.45rem 0.65rem;
      }

      .form-control {
        padding-right: 0.5rem;
        padding-left: 0.5rem;
        font-size: 0.85rem;
      }
    }
  }

  // Adjust scrollable menu height considering search box height
  .main-menu-content {
    height: calc(100% - 80px - 52px) !important;
  }

  // When sidebar is collapsed in desktop icon-only mode
  &:not(.expanded) {
    .menu-search {
      display: none;
    }
    .main-menu-content {
      height: calc(100% - 80px) !important;
    }
  }

  &.menu-dark {
    .menu-search {
      border-bottom: 1px solid rgba(255, 255, 255, 0.07);

      .input-group-merge {
        background-color: #161d31;
        border: 1px solid #3b4253;

        .input-group-text,
        .form-control {
          background-color: transparent !important;
          color: #d0d2d6 !important;
          border: none !important;
        }

        .form-control::placeholder {
          color: #676d7d;
        }

        .feather {
          color: #b4b7bd;
        }
      }
    }

    .no-results {
      color: #676d7d !important;
    }
  }

  &.menu-light {
    .menu-search {
      .input-group-merge {
        background-color: #f8f8f8;
        border: 1px solid #ebe9f1;

        .input-group-text,
        .form-control {
          background-color: transparent;
        }
      }
    }
  }
}
</style>
